import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isVerified } from "@/lib/verification";
import { toast } from "sonner";
import { ShieldCheck, Lock, Image, MessageCircle, Video, CalendarHeart, ArrowRight, RefreshCw } from "lucide-react";
import { ManualAgeCheck } from "@/components/ManualAgeCheck";

const UNLOCKS = [
  { icon: Image, label: "Profile photos", copy: "See every member's photos, unblurred where they allow it." },
  { icon: MessageCircle, label: "Messaging & calls", copy: "Chat, vanishing photos and cam-to-cam with your matches." },
  { icon: Video, label: "Creator rooms", copy: "Live streams, private sessions and gifting." },
  { icon: CalendarHeart, label: "Event details", copy: "Locations, hosts and guest lists for every soirée." },
];

export default function Verify() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null);
  const [stripeDown, setStripeDown] = useState(false);
  const from = loc.state?.from;

  useEffect(() => { if (isVerified(user)) nav(from || "/browse", { replace: true }); }, [user, from, nav]);

  const check = async (silent = false) => {
    setChecking(true);
    try {
      const { data } = await api.get("/identity/status");
      setStatus(data);
      if (data.age_verified) { await refresh(); toast.success("You're verified — welcome in"); return true; }
      if (!silent) toast(data.status === "processing" ? "Still processing — usually under a minute." : "Not verified yet.");
    } catch { /* ignore */ } finally { setChecking(false); }
    return false;
  };

  useEffect(() => {
    if (new URLSearchParams(loc.search).get("identity") !== "submitted") return;
    let tries = 0;
    toast("Checking your verification…");
    const poll = setInterval(async () => {
      tries += 1;
      const ok = await check(true);
      if (ok || tries >= 8) { clearInterval(poll); window.history.replaceState({}, "", "/verify"); }
    }, 2500);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/identity/session");
      if (data.already_verified) { await refresh(); return; }
      window.location.href = data.url;
    } catch (err) {
      if (err.response?.status === 409) setStripeDown(true);
      toast.error(err.response?.data?.detail || "Could not start verification");
      setStarting(false);
    }
  };

  useEffect(() => { check(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const failed = status?.status === "requires_input";
  const manualStatus = status?.manual_status;
  const showManual = stripeDown || manualStatus === "pending" || manualStatus === "rejected";

  return (
    <div data-testid="verify-page" className="pt-24 sm:pt-32 pb-24 max-w-4xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><Lock size={13} /> Verification required</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95]">
          The room is <em className="not-italic text-gold">members</em><br className="hidden sm:block" /> we can vouch for.
        </h1>
        <p className="text-text-muted mt-6 max-w-xl text-base">
          Velluto is a private, adults-only space. Before photos, messages, creators or event details open up, every
          member confirms they're 18+ with a government ID and a matching selfie — powered by Stripe Identity.
          We never see or store your documents.
        </p>

        <div className="mt-10 grid gap-5 lg:grid-cols-[1.1fr_1fr]">
          <div data-testid="verify-card" className="rounded-3xl border border-gold/30 bg-surface p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gold/10 blur-3xl" />
            <span className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center"><ShieldCheck size={24} className="text-gold" /></span>
            <h2 className="font-display text-3xl font-light mt-6">Verify in about two minutes</h2>
            <ul className="mt-4 space-y-2 text-sm text-text-muted">
              <li>1. Photograph your passport, licence or ID card</li>
              <li>2. Take a quick selfie so we know it's you</li>
              <li>3. Return here — the door opens automatically</li>
            </ul>
            {failed && (
              <p data-testid="verify-error" className="mt-5 text-sm text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-2xl px-4 py-3">
                We couldn't complete that attempt{status?.error_reason ? ` (${status.error_reason.replace(/_/g, " ")})` : ""}. Please try again with a clearer photo.
              </p>
            )}
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button data-testid="verify-start-btn" onClick={start} disabled={starting}
                className="flex-1 flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors disabled:opacity-50">
                <ShieldCheck size={16} /> {starting ? "Opening…" : failed ? "Try again" : "Verify I'm 18+"}
              </button>
              <button data-testid="verify-check-btn" onClick={() => check(false)} disabled={checking}
                className="flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.15em] hover:text-text-main transition-colors disabled:opacity-50">
                <RefreshCw size={14} className={checking ? "animate-spin" : ""} /> Check status
              </button>
            </div>
            <p className="text-[11px] text-text-muted mt-4">Encrypted end-to-end by Stripe. Velluto only receives a yes/no and your date of birth check.</p>
            {showManual && (
              <ManualAgeCheck status={manualStatus} reason={status?.manual_reason}
                onSubmitted={() => setStatus((s) => ({ ...(s || {}), manual_status: "pending" }))} />
            )}
          </div>

          <div className="rounded-3xl border border-white/10 bg-surface/60 p-7 sm:p-9">
            <p className="text-xs uppercase tracking-[0.2em] text-text-muted">What verification unlocks</p>
            <ul className="mt-5 space-y-5">
              {UNLOCKS.map(({ icon: Icon, label, copy }) => (
                <li key={label} className="flex gap-4">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center shrink-0"><Icon size={15} className="text-gold" /></span>
                  <div><p className="text-sm">{label}</p><p className="text-xs text-text-muted mt-0.5">{copy}</p></div>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-white/8 flex flex-wrap gap-x-5 gap-y-2 text-xs uppercase tracking-[0.15em]">
              <Link to="/me" data-testid="verify-link-profile" className="text-text-muted hover:text-gold transition-colors flex items-center gap-1">Finish your profile <ArrowRight size={12} /></Link>
              <Link to="/events" data-testid="verify-link-events" className="text-text-muted hover:text-gold transition-colors flex items-center gap-1">Peek at events <ArrowRight size={12} /></Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
